import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./ProgramManagement.module.scss";
import Header from "../../../components/header/Header";
import Button from "../../../components/button/Button";
import Footer from "../../../components/footer/Footer";
import IconArrowUp from 'src/assets/icons/dropdown.svg?react';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import { useToast } from "@src/hooks";
import DeleteModal from "@src/components/modal/delete-modal/DeleteModal";
import { useHistory, useLocation } from "react-router-dom";
import { SllProgram, useDeleteSllProgramMutation, useGetListMultiCollectionsQuery, useGetProgramsLazyQuery } from "@src/api/graphql-codegen";
import Spinner from "@src/components/spinner/Spinner";
import { hasArrayValues } from "@src/utils/helpers";
import { Collapse } from "react-collapse";
import ProgramItem from "./programItem/ProgramItem";
import { defaultProgram } from "./edit/ProgramEdit";
import { SelectOption } from "@src/types";
import { logger } from '../../../utils/logger';

type Props = {};

function ProgramManagement(props: Props) {

  const [selProgram, setSelProgram] = useState<any>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selected, setSelected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [emptySelect, setEmptySelect] = useState(false);

  const history = useHistory();
  const location = useLocation<{id: string, mcId: string}>();
  const id = location.state?.id;
  const mcId = location.state?.mcId;

  const { successToast, errorToast } = useToast();

  const {data: collectionData, loading: allCollectionLoading} = useGetListMultiCollectionsQuery({
    variables: { input: { keyword: '', ageGroup: '', country: '' }},
    fetchPolicy: 'network-only'
  })

  const [getPrograms, {data, loading, refetch}] = useGetProgramsLazyQuery({
    fetchPolicy: 'no-cache'
  })

  const [deleteProgram, {loading: removing}] = useDeleteSllProgramMutation({
    onCompleted(data, clientOptions) {
      if (data.sllDeleteProgram) {
        successToast("Program has been removed successfully!");
        refetch();
        setTimeout(() => setShowDeleteModal(false), 100);
      } else {
        errorToast("Remove failed, Please try again!");
        setTimeout(() => setShowDeleteModal(false), 100);
        refetch();
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      errorToast("Remove failed, Please try again!");
      setTimeout(() => setShowDeleteModal(false), 100);
    }
  })

  useEffect(() => {
    getPrograms();
  }, [getPrograms])

  useEffect(() => {
    if (id && data?.getPrograms) {
      setSelectedId(id);
      setSelected(true);
      setTimeout(() => {
        const courseElement = document.getElementById(id);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 1000)
    }
  }, [id, data?.getPrograms])

  const allMCCollections = useMemo(() => {
    if (!collectionData?.listNormalMultiCollections) return [];
    var filteredCollections = [...(collectionData?.listNormalMultiCollections ?? [])].sort((a, b) => (a?.name ?? '').localeCompare((b?.name ?? '')));
    const mcCollections = filteredCollections.map(mc => {
      return {
        label: `${mc?.name} | ${mc?.id} | ${mc?.grade}`,
        value: mc?.id
      }
    })
    return mcCollections
  }, [collectionData?.listNormalMultiCollections])

  const allPrograms = useMemo(() => {
    if (!data?.getPrograms) return [];
    var sortPrograms = [...data.getPrograms].sort((a, b) => (a?.name ?? '').localeCompare((b?.name ?? '')));
    return sortPrograms;
  }, [data?.getPrograms])

  const handleCreateProgram = () => {
    history.push({ pathname: '/super/program-management/edit/new', state: { programData: defaultProgram } });
  }

  const handleDeleteProgram = (program: any) => {
    setSelProgram(program);
    setShowDeleteModal(true);
  }

  const handleRemoveProgram = useCallback(() => {
    if (selProgram) {
      deleteProgram({
        variables: {
          input: {
            id: selProgram.id
          }
        }
      })  
    }
    
  }, [selProgram, deleteProgram])

  const handleSelect = useCallback((programId: string) => {
    if (selectedId === programId) {
      setSelected(false);
      setSelectedId(null);
    } else {
      setSelected(true);
      setSelectedId(programId);
      setTimeout(() => {
        const courseElement = document.getElementById(programId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }, [selectedId, setSelected, setSelectedId])

  const handleEmptyClick = () => {
    setEmptySelect(current => !current);
  }

  const handleRefresh = () => {
    getPrograms();
  }

  return (
    <>
      <Header />
      <div className={styles.page}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div>
              <h2 className={styles.title}>Program Management</h2>
            </div>
            <div className={styles.rightHeader}>
              <div className={styles.search}></div>
              <Button
                onClick={handleCreateProgram}
                className={styles.addBtn}
                label={"Create Program"}
              />
            </div>
          </div>
          {loading || allCollectionLoading ? <div className={styles.spinner}><Spinner/></div> : <>
            <div className={styles.collectionContainer}>
              <div className={styles.collectionCardsContainer}>
                {hasArrayValues(allPrograms) ? allPrograms.map((item, index) => {
                  return (
                    <div key={index} style={{position: 'relative'}}>
                      <ProgramItem
                        selected={selected}
                        selectedId={selectedId}
                        programId={`${item?.id}`}
                        program={item as SllProgram}
                        mcCollectionOptions={allMCCollections as SelectOption[]}
                        mcId={mcId}
                        handleSeleted={handleSelect}
                        handleDelete={handleDeleteProgram}
                        refresh={handleRefresh}
                      />
                    </div>
                  )
                }) : (
                  <div className={styles.programCard}>
                    <div className={styles.titleSection} onClick={handleEmptyClick}>
                      <IconArrowUp className={[styles.arrowIcon, emptySelect ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={24} height={24} />
                      <h1>{'{{ProgramName}}'}</h1>
                    </div>
                    <Collapse isOpened={emptySelect}>
                      <div>
                        <div className={styles.programContainer}>
                          <div className={styles.programOverview}>
                            <div className={styles.greyTitleView}>
                              <h2>Program Overview</h2>
                            </div>
                            <div className={styles.overviewEmpty}>
                              <h3>No Program Overview Available</h3>
                            </div>
                            <div className={styles.buttonView}>
                              <Button size="small" label="Create Program Overview" className={styles.createButton} outline color="white" onClick={handleCreateProgram} leftIcon={<IconPencil fill="orange" />} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Collapse>
                  </div>
                )}
              </div>
            </div>
          </>}
        </div>
      </div>
      <Footer />
      {showDeleteModal && <DeleteModal open={showDeleteModal} onClose={() => {setShowDeleteModal(false)}} loading={removing} onDelete={handleRemoveProgram} message={`Are you sure you want to delete ${selProgram?.name}?`} cancelLabel={'NO'} deleteLabel={'DELETE'}/>}
    </>
  );
}

export default ProgramManagement;
